import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(identifier.trim(), password);
      if (user.role !== "admin") {
        toast.error("Acceso restringido al administrador");
        setLoading(false);
        return;
      }
      toast.success(`Bienvenido, ${user.name}`);
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2540] text-white flex items-center justify-center p-6 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(900px 500px at 20% 0%, rgba(180,151,90,0.15), transparent), radial-gradient(700px 400px at 100% 100%, rgba(255,255,255,0.05), transparent)",
        }}
      />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white mb-8"
          data-testid="admin-back-home"
        >
          <ArrowLeft size={12} className="mr-1.5" /> Volver al inicio
        </Link>

        <div className="border border-white/15 bg-white/[0.04] backdrop-blur-sm rounded-sm p-8 lg:p-10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-[#B4975A] flex items-center justify-center">
              <ShieldCheck size={20} className="text-[#0A2540]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                Acceso restringido
              </div>
              <div className="font-heading text-lg font-bold">
                Panel de Administración
              </div>
            </div>
          </div>

          <h1 className="mt-8 font-heading text-3xl font-bold leading-tight">
            Bienvenido,
            <br />
            <span className="text-[#B4975A]">administrador.</span>
          </h1>
          <p className="mt-3 text-sm text-white/70">
            Esta entrada es de uso exclusivo. Verifique sus credenciales corporativas antes de
            continuar.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="admin-identifier"
                className="text-[10px] uppercase tracking-[0.25em] text-white/60"
              >
                Correo corporativo
              </Label>
              <Input
                id="admin-identifier"
                data-testid="admin-login-identifier-input"
                type="email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="admin@smartnation.cl"
                className="rounded-sm h-11 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#B4975A]"
                autoComplete="username"
                required
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="admin-password"
                className="text-[10px] uppercase tracking-[0.25em] text-white/60"
              >
                Contraseña
              </Label>
              <Input
                id="admin-password"
                data-testid="admin-login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm h-11 bg-white/5 border-white/15 text-white focus-visible:ring-[#B4975A]"
                autoComplete="current-password"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full h-11 bg-[#B4975A] hover:bg-[#9c814a] text-[#0A2540] rounded-sm font-semibold"
            >
              {loading ? "Ingresando…" : "Ingresar al panel"}
            </Button>
          </form>

          <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-white/50">
            <Lock size={12} /> Conexión cifrada · SmartNation S.A.
          </div>
        </div>
      </div>
    </div>
  );
}
